import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
    createExportProfile,
    updateExportProfile,
    deleteExportProfile,
    getExportProfile,
    getExportUserFields,
    defaultCalculation,
    defaultColumnsForLayout,
    resolveExportColumnHeader,
} from "../../../../_api/ExportProfile";
import { getHourTypes } from "../../../../_api/HourType";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import { Button } from "../../../../_helpers/components/Button";
import { BackButton } from "../../../../_helpers/components/BackButton";
import { Loader } from "../../../../_helpers/components/Forms/Loader";
import { ExportCalculationEditor } from "./ExportCalculationEditor";
import { ExportColumnBuilder } from "./ExportColumnBuilder";

const LAYOUT_OPTIONS = [
    { value: "multi_column", label: "Multicolonnes" },
    { value: "one_row_per_record", label: "1 ligne / enregistrement" },
];

const TABS = [
    { id: "general", label: "Général" },
    { id: "calculations", label: "Calculs" },
    { id: "columns", label: "Colonnes" },
];

export const ExportProfileForm = () => {
    const navigate = useNavigate();
    const { profile_id } = useParams();
    const [activeTab, setActiveTab] = useState("general");
    const [isLoading, setIsLoading] = useState(!!profile_id);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [name, setName] = useState("");
    const [layoutMode, setLayoutMode] = useState("multi_column");
    const [separator, setSeparator] = useState(";");
    const [dateFormat, setDateFormat] = useState("Ymd");
    const [utf8Bom, setUtf8Bom] = useState(true);
    const [includeHeader, setIncludeHeader] = useState(true);
    const [calculations, setCalculations] = useState([defaultCalculation()]);
    const [columns, setColumns] = useState(defaultColumnsForLayout("multi_column", ["CALC_CONGES"]));
    const [userFieldOptions, setUserFieldOptions] = useState([]);
    const [hourTypes, setHourTypes] = useState([]);

    useEffect(() => {
        Promise.all([getExportUserFields(), getHourTypes()])
            .then(([fieldsResp, hourTypesResp]) => {
                setUserFieldOptions(fieldsResp.data?.data || fieldsResp.data || []);
                setHourTypes(hourTypesResp.data || []);
            })
            .catch(() => toast.error("Erreur lors du chargement des données."));
    }, []);

    useEffect(() => {
        if (!profile_id) return;

        setIsLoading(true);
        getExportProfile(profile_id)
            .then((resp) => {
                const data = resp.data?.data || resp.data;
                setName(data.name);
                setLayoutMode(data.layout_mode);
                setSeparator(data.separator || ";");
                setDateFormat(data.date_format || "Ymd");
                setUtf8Bom(data.utf8_bom !== false);
                setIncludeHeader(data.include_header !== false);
                setCalculations(data.calculations || [defaultCalculation()]);
                setColumns(
                    (data.columns || [])
                        .sort((a, b) => a.position - b.position)
                        .map((col) => ({
                            ...col,
                            header: col.header || resolveExportColumnHeader(col.value),
                        }))
                );
            })
            .catch(() => {
                toast.error("Profil introuvable.");
                navigate("/company/exports");
            })
            .finally(() => setIsLoading(false));
    }, [profile_id, navigate]);

    const handleLayoutChange = (value) => {
        setLayoutMode(value);
        const names = calculations.map((c) => c.name).filter(Boolean);
        setColumns(defaultColumnsForLayout(value, names.length ? names : ["CALC_CONGES"]));
    };

    const handleCalculationsChange = (nextCalculations) => {
        setCalculations(nextCalculations);
        if (layoutMode === "multi_column") {
            const names = nextCalculations.map((c) => c.name).filter(Boolean);
            setColumns(defaultColumnsForLayout("multi_column", names));
        }
    };

    const handleDelete = async () => {
        if (!profile_id || isDeleting) return;

        const label = name || "ce profil";
        if (
            !window.confirm(
                `Supprimer le profil « ${label} » ? Cette action est irréversible.`
            )
        ) {
            return;
        }

        setIsDeleting(true);
        try {
            await deleteExportProfile(profile_id);
            toast.success(`Le profil « ${label} » a été supprimé.`);
            navigate("/company/exports");
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                "Erreur lors de la suppression du profil.";
            toast.error(message);
        } finally {
            setIsDeleting(false);
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const payload = {
            name,
            layout_mode: layoutMode,
            separator,
            date_format: dateFormat,
            utf8_bom: utf8Bom,
            include_header: includeHeader,
            calculations,
            columns,
        };

        try {
            if (profile_id) {
                await updateExportProfile(profile_id, payload);
                toast.success("Profil mis à jour.");
            } else {
                await createExportProfile(payload);
                toast.success("Profil créé.");
            }
            navigate("/company/exports");
        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.response?.data?.errors?.[0] ||
                "Erreur lors de l'enregistrement.";
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return <Loader />;
    }

    const calculationNames = calculations.map((c) => c.name).filter(Boolean);

    return (
        <div className="min-h-full bg-gray-50 pb-24">
            <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-20">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                            <BackButton />
                            <div className="min-w-0">
                                <h1 className="text-lg font-bold text-gray-900 truncate">
                                    {profile_id ? "Modifier le profil" : "Nouveau profil d'export"}
                                </h1>
                                {name && (
                                    <p className="text-xs text-gray-500 truncate">{name}</p>
                                )}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            {profile_id && (
                                <Button
                                    type="red"
                                    action={handleDelete}
                                    name="Supprimer"
                                    loading={isDeleting}
                                />
                            )}
                            <Button
                                type="empty"
                                action={() => navigate("/company/exports")}
                                name="Annuler"
                            />
                            <Button
                                action={handleSave}
                                name="Enregistrer"
                                loading={isSaving}
                            />
                        </div>
                    </div>
                    <nav className="mt-3 flex gap-1 border-b border-gray-200 -mb-px">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={[
                                    "px-3 py-2 text-sm font-medium border-b-2 transition-colors",
                                    activeTab === tab.id
                                        ? "border-app-primary text-app-primary"
                                        : "border-transparent text-gray-500 hover:text-gray-700",
                                ].join(" ")}
                            >
                                {tab.label}
                                {tab.id === "calculations" && (
                                    <span className="ml-1.5 text-xs text-gray-400">
                                        ({calculations.length})
                                    </span>
                                )}
                                {tab.id === "columns" && (
                                    <span className="ml-1.5 text-xs text-gray-400">
                                        ({columns.length})
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 py-5">
                {activeTab === "general" && (
                    <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5 space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div className="sm:col-span-2">
                                <InputForm name="Nom du profil" value={name} set={setName} />
                            </div>
                            <DefaultDropdown
                                name="Format des lignes"
                                value={layoutMode}
                                options={LAYOUT_OPTIONS}
                                onChange={(e) => handleLayoutChange(e.target.value)}
                            />
                            <InputForm
                                name="Séparateur"
                                value={separator}
                                set={setSeparator}
                            />
                            <InputForm
                                name="Format dates"
                                value={dateFormat}
                                set={setDateFormat}
                            />
                        </div>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 pt-1">
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300"
                                    checked={utf8Bom}
                                    onChange={(e) => setUtf8Bom(e.target.checked)}
                                />
                                BOM UTF-8 (Excel)
                            </label>
                            <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded border-gray-300"
                                    checked={includeHeader}
                                    onChange={(e) => setIncludeHeader(e.target.checked)}
                                />
                                Ligne d'entête
                            </label>
                        </div>
                    </section>
                )}

                {activeTab === "calculations" && (
                    <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5">
                        <p className="text-xs text-gray-500 mb-4">
                            Définissez les rubriques calculées à partir des créneaux planning.
                        </p>
                        <ExportCalculationEditor
                            calculations={calculations}
                            onChange={handleCalculationsChange}
                            hourTypes={hourTypes}
                        />
                    </section>
                )}

                {activeTab === "columns" && (
                    <section className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5">
                        <p className="text-xs text-gray-500 mb-4">
                            Ordre et libellés des colonnes du fichier exporté.
                        </p>
                        <ExportColumnBuilder
                            columns={columns}
                            onChange={setColumns}
                            layoutMode={layoutMode}
                            userFieldOptions={userFieldOptions}
                            calculationNames={calculationNames}
                        />
                    </section>
                )}
            </div>
        </div>
    );
};
